import { DATA_SERVICE_BASE_URL } from './ApiService';
import { fetchWithAuth } from './AuthService';

/**
 * Audio URL Service - Manages signed URL caching and refresh for audio streaming
 * 
 * Features:
 * - In-memory URL cache with expiration tracking
 * - Automatic refresh when URLs are expired or near expiration
 * - Error recovery with retry logic
 * - Supports both document IDs and audio content IDs
 */

// In-memory cache: Map<documentId, { url: string, expiresAt: Date }>
const urlCache = new Map();

// Buffer time before expiration to proactively refresh (5 minutes)
const EXPIRATION_BUFFER_MS = 5 * 60 * 1000;

/**
 * Fetch a fresh signed streaming URL from data-service
 * @param {number} documentId - Document ID
 * @param {number} [expirationSeconds=3600] - URL expiration time in seconds
 * @returns {Promise<{url: string, expiresAt: Date}>} URL and expiration timestamp
 */
export const fetchStreamUrl = async (documentId, expirationSeconds = 3600) => {
  try {
    const response = await fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/storage/audio/stream-url?documentId=${documentId}&expiration=${expirationSeconds}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch stream URL: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success || !data.streamUrl) {
      throw new Error(data.error || 'No stream URL returned');
    }

    // Parse expiration timestamp (ISO-8601 from Java LocalDateTime)
    const expiresAt = data.expiresAt ? new Date(data.expiresAt) : new Date(Date.now() + expirationSeconds * 1000);

    return {
      url: data.streamUrl,
      expiresAt,
      metadata: data.audioMetadata
    };
  } catch (error) {
    console.error('[AudioUrlService] Failed to fetch stream URL:', error);
    throw error;
  }
};

/**
 * Check if a cached URL is still valid (not expired)
 * @param {Object} cachedEntry - Cached URL entry
 * @returns {boolean} True if URL is still valid
 */
const isUrlValid = (cachedEntry) => {
  if (!cachedEntry || !cachedEntry.expiresAt) {
    return false;
  }

  const now = Date.now();
  const expirationTime = cachedEntry.expiresAt.getTime();

  // Consider URL invalid if it's within buffer time of expiration
  return expirationTime - now > EXPIRATION_BUFFER_MS;
};

/**
 * Get streaming URL from cache or fetch fresh if expired
 * @param {number} documentId - Document ID
 * @param {boolean} [forceRefresh=false] - Force fetch even if cached URL is valid
 * @returns {Promise<string>} Streaming URL
 */
export const getOrRefreshUrl = async (documentId, forceRefresh = false) => {
  const cachedEntry = urlCache.get(documentId);

  // Return cached URL if valid and not forcing refresh
  if (!forceRefresh && cachedEntry && isUrlValid(cachedEntry)) {
    console.log(`[AudioUrlService] Using cached URL for document ${documentId}`);
    return cachedEntry.url;
  }

  // Fetch fresh URL
  console.log(`[AudioUrlService] Fetching fresh URL for document ${documentId}`);
  const { url, expiresAt } = await fetchStreamUrl(documentId);

  // Cache the new URL
  urlCache.set(documentId, { url, expiresAt });

  return url;
};

/**
 * Fetch a signed download URL with Content-Disposition: attachment
 * @param {number} documentId - Document ID
 * @returns {Promise<string>} Download URL that triggers browser download
 */
export const fetchDownloadUrl = async (documentId) => {
  const response = await fetchWithAuth(
    `${DATA_SERVICE_BASE_URL}/api/storage/audio/stream-url?documentId=${documentId}&download=true`,
    { method: 'GET' }
  );
  if (!response.ok) throw new Error(`Failed to fetch download URL: ${response.status}`);
  const data = await response.json();
  if (!data.success || !data.streamUrl) throw new Error(data.error || 'No download URL returned');
  return data.streamUrl;
};

/**
 * Handle audio playback error with automatic URL refresh retry
 * @param {number} documentId - Document ID
 * @param {HTMLAudioElement} audioElement - Audio element that failed
 * @param {Function} onSuccess - Callback when retry succeeds with new URL
 * @param {Function} onFailure - Callback when retry fails
 */
export const handlePlaybackError = async (documentId, audioElement, onSuccess, onFailure) => {
  console.log(`[AudioUrlService] Handling playback error for document ${documentId}`);

  try {
    // Fetch fresh URL (force refresh)
    const newUrl = await getOrRefreshUrl(documentId, true);

    // Update audio source
    audioElement.src = newUrl;
    audioElement.load();

    console.log(`[AudioUrlService] Retrying playback with fresh URL`);

    if (onSuccess) {
      onSuccess(newUrl);
    }
  } catch (error) {
    console.error(`[AudioUrlService] Failed to recover from playback error:`, error);

    if (onFailure) {
      onFailure(error);
    }
  }
};

/**
 * Clear cached URL for a specific document
 * @param {number} documentId - Document ID
 */
export const clearCachedUrl = (documentId) => {
  urlCache.delete(documentId);
};

/**
 * Clear all cached URLs
 */
export const clearAllCachedUrls = () => {
  urlCache.clear();
};

/**
 * Get cache statistics (for debugging)
 * @returns {Object} Cache statistics
 */
export const getCacheStats = () => {
  return {
    size: urlCache.size,
    entries: Array.from(urlCache.entries()).map(([docId, entry]) => ({
      documentId: docId,
      expiresAt: entry.expiresAt,
      isValid: isUrlValid(entry),
      minutesUntilExpiry: entry.expiresAt ? Math.round((entry.expiresAt.getTime() - Date.now()) / 60000) : null
    }))
  };
};

const AudioUrlService = {
  fetchStreamUrl,
  getOrRefreshUrl,
  fetchDownloadUrl,
  handlePlaybackError,
  clearCachedUrl,
  clearAllCachedUrls,
  getCacheStats
};
export default AudioUrlService;